import { z } from "zod";
import { DocumentFieldTypeSchema } from "./clinic-documents.js";
import { FORM_ORDER_SET_LIMITS, FormOrderSetSchema } from "./form-order-sets.js";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
const KeySchema = z.string().trim().min(1).max(160);
const TextSchema = z.string().trim().min(1).max(4_000);
/** Stored separately from the legacy PDF version, whose hash must remain unchanged. */
export const FormInstructionInputSchema = z.strictObject({
    id: KeySchema,
    scope: z.enum(["form", "clinic"]),
    documentId: IdSchema.nullable(),
    fieldId: KeySchema.nullable(),
    text: TextSchema,
    /** Optional to retain the serialized identity of existing persisted instructions. */
    references: z
        .array(z.strictObject({ documentId: IdSchema, fieldIds: z.array(KeySchema).max(100) }))
        .max(20)
        .optional(),
    /**
     * Administrator-reviewed ordered-test wording, available at form and clinic scope. It matches a
     * reviewed order item whose name contains it as whole words, never note keywords or inferred codes.
     */
    orderReferences: z
        .array(z.strictObject({
        kind: z.literal("exact_order_text"),
        // Punctuation alone names no test and would match almost every ordered test.
        text: z
            .string()
            .trim()
            .min(1)
            .max(240)
            .regex(/[\p{L}\p{N}]/u, "Enter an ordered test's name, using letters or numbers."),
    }))
        .max(100)
        .optional(),
    /**
     * Present on a clinic order set. Its text is generated from the set's name, and its actions are
     * compiled from the set by the server, never by the model.
     */
    orderSet: FormOrderSetSchema.optional(),
});
export const FormInstructionDocumentRefSchema = z.strictObject({
    documentId: IdSchema,
    documentVersionId: IdSchema,
    versionHash: Sha256Schema,
});
export const FormInstructionFieldRefSchema = FormInstructionDocumentRefSchema.extend({
    fieldId: KeySchema,
});
export const FormInstructionCatalogFormSchema = FormInstructionDocumentRefSchema.extend({
    name: z.string().trim().min(1).max(240),
    includedPageNumbers: z.array(z.number().int().positive()).min(1).max(500),
    fields: z
        .array(z.strictObject({
        fieldId: KeySchema,
        label: z.string().trim().min(1).max(500),
        pageNumber: z.number().int().positive(),
        fieldType: DocumentFieldTypeSchema,
        authoritative: z.boolean(),
        sourceKey: KeySchema.nullable(),
        clinicalConceptId: KeySchema.nullable(),
        requiresClinicalConcept: z.boolean().optional(),
        /** Present only when a reviewed mapping (record, order, or transform) fills the field. */
        mapped: z.literal(true).optional(),
    }))
        .max(2_000),
});
export const ReviewedFormInstructionOverrideSchema = z.strictObject({
    instructionId: KeySchema,
    overridesInstructionId: KeySchema,
    target: FormInstructionFieldRefSchema,
    reason: z.string().trim().min(1).max(1_000),
    reviewedBy: IdSchema,
    reviewedAt: IsoDateTimeSchema,
});
export const FormInstructionCompileInputSchema = z.strictObject({
    instructions: z.array(FormInstructionInputSchema).max(200),
    forms: z.array(FormInstructionCatalogFormSchema).max(100),
    sources: z.array(z.strictObject({ key: KeySchema, label: TextSchema })).max(200),
    concepts: z.array(z.strictObject({ id: KeySchema, label: TextSchema })).max(2_000),
    scenarios: z.array(z.strictObject({ id: KeySchema, label: TextSchema })).max(200),
    reviewedOverrides: z.array(ReviewedFormInstructionOverrideSchema).max(100).default([]),
});
export const FormInstructionCompilationInputSchema = FormInstructionCompileInputSchema;
/** Predicates match approved structured concepts only; there is no fuzzy clinical matching. */
export const FormInstructionConditionSchema = z.discriminatedUnion("kind", [
    z.strictObject({ kind: z.literal("always") }),
    z.strictObject({
        kind: z.literal("concepts"),
        mode: z.enum(["any", "all", "none"]),
        conceptIds: z.array(KeySchema).min(1).max(100),
    }),
    z.strictObject({ kind: z.literal("scenario"), scenarioId: KeySchema }),
]);
const conditioned = { condition: FormInstructionConditionSchema };
export const FormInstructionActionSchema = z.discriminatedUnion("kind", [
    z.strictObject({
        kind: z.literal("copy"),
        ...conditioned,
        target: FormInstructionFieldRefSchema,
        sourceKey: KeySchema,
        format: z.enum(["identity", "date_iso", "date_dd_mm_yyyy"]),
    }),
    z.strictObject({
        kind: z.literal("checkbox"),
        ...conditioned,
        target: FormInstructionFieldRefSchema,
        checked: z.boolean(),
        clinicalConceptId: KeySchema.nullable(),
    }),
    z.strictObject({
        kind: z.literal("requested_test_text"),
        ...conditioned,
        target: FormInstructionFieldRefSchema,
        conceptIds: z.array(KeySchema).min(1).max(100),
        separator: z.enum([", ", "; ", "\n"]),
    }),
    z.strictObject({
        kind: z.literal("narrative"),
        ...conditioned,
        target: FormInstructionFieldRefSchema,
        sourceKeys: z.array(KeySchema).min(1).max(30),
        maxCharacters: z.number().int().positive().max(4_000),
    }),
    z.strictObject({
        kind: z.literal("propose_orders"),
        ...conditioned,
        conceptIds: z.array(KeySchema).min(1).max(100),
    }),
    z.strictObject({
        kind: z.literal("select_forms"),
        ...conditioned,
        mode: z.enum(["include", "exclude"]),
        forms: z.array(FormInstructionDocumentRefSchema).min(1).max(20),
    }),
    z.strictObject({
        kind: z.literal("approved_test_list"),
        ...conditioned,
        target: FormInstructionFieldRefSchema,
        conceptIds: z.array(KeySchema).min(1).max(100),
        separator: z.enum([", ", "; ", "\n"]),
    }),
    /**
     * Writes nothing. It records that an administrator wants an ordinary field left for the
     * clinician, so any other instruction writing that field under an overlapping condition is a
     * conflict. It can never suppress patient facts, mapped sources, or coded order boxes.
     * Keep it at option 7: the provider wire schema lists options 0-7 by index.
     */
    z.strictObject({
        kind: z.literal("leave_blank"),
        ...conditioned,
        target: FormInstructionFieldRefSchema,
    }),
    /*
     * The kinds below are compiled from clinic order sets by the server only. The model's wire
     * schema never offers them, and validation refuses them outside an order set.
     */
    /** Writes an order set's fixed text ("Urine") into an ordinary text field. */
    z.strictObject({
        kind: z.literal("fixed_text"),
        ...conditioned,
        target: FormInstructionFieldRefSchema,
        text: z.string().trim().min(1).max(240),
    }),
    /**
     * Writes one test on the first free line of an ordered list of text fields (`target` is the
     * first line). Lines another instruction filled or keeps blank are skipped.
     */
    z.strictObject({
        kind: z.literal("list_line"),
        ...conditioned,
        target: FormInstructionFieldRefSchema,
        lines: z.array(FormInstructionFieldRefSchema).min(1).max(30),
        conceptIds: z.array(KeySchema).length(1),
        text: z.string().trim().min(1).max(240),
    }),
    /**
     * Leaves order-set tests off these forms' own filling: no box is ticked for them by code or by
     * name, and they are not written on the forms' ordered-test lists (Other Tests lines, requested
     * tests). Values instructions write are unaffected. Compiled once per set form, gated on the set
     * alone; only tests on the order are left off.
     */
    z.strictObject({
        kind: z.literal("omit_automatic_test"),
        ...conditioned,
        forms: z.array(FormInstructionDocumentRefSchema).min(1).max(20),
        conceptIds: z.array(KeySchema).min(1).max(FORM_ORDER_SET_LIMITS.tests),
    }),
]);
export const FormInstructionStatusSchema = z.enum([
    "supported",
    "needs_clarification",
    "conflicting",
]);
export const FormInstructionIssueSchema = z.strictObject({
    code: z.enum([
        "unsupported_instruction",
        "omitted_clause",
        "unknown_reference",
        "excluded_page",
        "invalid_action",
        "protected_field",
        "clinical_expansion",
        "conflicting_actions",
        "missing_data",
        "approval_required",
        "stale_configuration",
        "unresolved_instruction",
        /** A sentence that changes nothing about filling; always a visible warning. */
        "no_effect",
    ]),
    severity: z.enum(["blocking", "warning"]),
    message: TextSchema,
    instructionId: KeySchema.nullable(),
    clauseId: KeySchema.nullable(),
    fieldId: KeySchema.nullable(),
});
/** The model cannot supply review evidence, original text, hashes, or executable code. */
export const FormInstructionProposalSchema = z.strictObject({
    clauses: z
        .array(z.strictObject({
        instructionId: KeySchema,
        clauseId: KeySchema,
        status: FormInstructionStatusSchema,
        explanation: TextSchema,
        actions: z.array(FormInstructionActionSchema).max(50),
        questions: z.array(TextSchema).max(20),
    }))
        .max(1_000),
});
export const FormInstructionCompilationSchema = z.strictObject({
    schemaVersion: z.literal(1),
    status: z.enum(["ready", "needs_review"]),
    inputHash: Sha256Schema,
    instructions: z
        .array(FormInstructionInputSchema.extend({
        status: FormInstructionStatusSchema,
        explanation: z.string().max(20_000),
        issues: z.array(FormInstructionIssueSchema).max(2_000),
        clauses: z
            .array(z.strictObject({
            id: KeySchema,
            text: TextSchema,
            status: FormInstructionStatusSchema,
            explanation: TextSchema,
            actions: z.array(FormInstructionActionSchema).max(50),
            issues: z.array(FormInstructionIssueSchema).max(100),
        }))
            .max(200),
    }))
        .max(200),
    issues: z.array(FormInstructionIssueSchema).max(10_000),
});
export const FormInstructionRuntimeContextSchema = z.strictObject({
    approvedConceptIds: z.array(KeySchema).max(500),
    approvedTests: z
        .array(z.strictObject({
        identity: KeySchema,
        display: z.string().min(1).max(500),
        conceptIds: z.array(KeySchema).max(100),
        /**
         * The clinic's names for this test on its forms (its active test names, such as "RPR"
         * written as "Syphilis"), each with the reviewed rule wording it matched that the test's
         * own name did not. Absent when no test name applies.
         */
        formNames: z
            .array(z.strictObject({
            name: z.string().trim().min(1).max(240),
            conceptIds: z.array(KeySchema).min(1).max(100),
        }))
            .max(20)
            .optional(),
    }))
        .max(500)
        .optional(),
    clinicalScenarioIds: z.array(KeySchema).max(200),
    /**
     * Suggested tests the physician explicitly chose not to order. They are never approved: their
     * boxes stay unticked and their names are never written. Absent when nothing was declined.
     */
    declinedConceptIds: z.array(KeySchema).max(500).optional(),
    sourceValues: z.record(KeySchema, z.union([z.string().max(20_000), z.number().finite(), z.boolean(), z.null()])),
});
export const FormInstructionExecutionSchema = z.strictObject({
    status: z.enum(["ready", "needs_review"]),
    selectedForms: z.array(FormInstructionDocumentRefSchema).max(100),
    excludedForms: z.array(FormInstructionDocumentRefSchema).max(100),
    values: z
        .array(z.strictObject({
        target: FormInstructionFieldRefSchema,
        value: z.union([z.string(), z.number(), z.boolean()]),
        instructionId: KeySchema,
        explanation: TextSchema,
        /** Clinical work represented by this exact deterministic field value, never inferred from prose. */
        representedConceptIds: z.array(KeySchema).max(500).optional(),
    }))
        .max(5_000),
    proposedConceptIds: z.array(KeySchema).max(500),
    narrativeRequests: z
        .array(z.strictObject({
        target: FormInstructionFieldRefSchema,
        instructionId: KeySchema,
        instructions: z.array(z.strictObject({ id: KeySchema, text: TextSchema })).max(200),
        sourceValues: FormInstructionRuntimeContextSchema.shape.sourceValues,
        maxCharacters: z.number().int().positive().max(4_000),
    }))
        .max(500),
    issues: z.array(FormInstructionIssueSchema).max(10_000),
    /**
     * Ordered tests an applied order set leaves off each form's own boxes and ordered-test lists,
     * because the set places them on another of its forms. Absent when nothing is left off.
     */
    automaticOmissions: z
        .array(z.strictObject({
        form: FormInstructionDocumentRefSchema,
        conceptIds: z.array(KeySchema).min(1).max(100),
        instructionId: KeySchema,
    }))
        .max(100)
        .optional(),
});
//# sourceMappingURL=form-instructions.js.map